import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';

/**
 * QR-on-paper card.
 *
 * Renders an SVG QR code with a clean printable layout that an operator can
 * stick at their counter, on the entrance door, or hand out as a flyer.
 *
 * Premium-logical decisions:
 *   - SVG render (not canvas) so print resolution is unbounded — no
 *     pixelation on A4.
 *   - The card includes the brand name + outlet name + the human-readable
 *     URL underneath the QR — if the QR fails to scan, the customer can
 *     type it in.
 *   - Generation is async; we show a small skeleton until ready instead of
 *     an empty box. Error state speaks plainly ("Could not draw the QR
 *     code. Reload the page.") — no stack trace.
 *   - Error correction level "H" (30%) so a stuck-on logo or a smudge
 *     still scans.
 */
export function QrPrintCard({
  url,
  brandName,
  outletName,
  size = 360,
}: {
  readonly url: string;
  readonly brandName: string;
  readonly outletName: string;
  readonly size?: number;
}) {
  const { t } = useI18n();
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setSvgMarkup(null);
    setError(null);
    QRCode.toString(url, {
      type: 'svg',
      errorCorrectionLevel: 'H',
      margin: 1,
      color: { dark: '#0f172a', light: '#ffffff' },
    })
      .then((svg) => {
        if (cancelled) return;
        setSvgMarkup(svg);
      })
      .catch((e) => {
        logger.error('qr generation failed', { url, error: String(e) });
        if (cancelled) return;
        setError(t('dashboard.qrCard.drawError'));
      });
    return () => {
      cancelled = true;
    };
  }, [url, t]);

  return (
    <article
      className="qr-print-card"
      style={{
        width: '100%',
        maxWidth: 480,
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-lg)',
        background: '#ffffff',
        color: '#0f172a',
        border: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-2)',
        textAlign: 'center',
        display: 'grid',
        gap: 'var(--space-4)',
      }}
    >
      <header>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {brandName}
        </p>
        <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}>
          {outletName}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'rgba(15,23,42,0.7)',
            margin: 'var(--space-2) 0 0 0',
          }}
        >
          {t('dashboard.qrCard.scanToGetInLine')}
        </p>
      </header>

      <div
        ref={containerRef}
        aria-label={t('dashboard.qrCard.qrCodeAria')}
        style={{
          // Fluid, capped at `size` — NOT a fixed `size` square.
          //
          // It was `width: size, height: size` with size defaulting to 360, and
          // the card adds `--space-6` of padding either side, so the card
          // measured ~385px wide and overflowed every phone at or below 400px.
          // The operator's printable QR — the page they open to show a customer
          // — did not fit the operator's own phone. Caught 2026-08-23 by
          // `assertNoHOverflow` at the 360px viewport, on this page's first
          // browser test (measured: scrollWidth 385 > clientWidth 360).
          //
          // `aspectRatio` keeps it square while it shrinks, so the QR stays
          // scannable, and `maxWidth` preserves the intended print size.
          width: '100%',
          maxWidth: size,
          aspectRatio: '1 / 1',
          margin: '0 auto',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        // The qrcode lib returns a complete <svg> element string. Setting
        // innerHTML keeps it a real DOM SVG (so it scales crisply at any
        // print size) without a wrapper <img>.
        dangerouslySetInnerHTML={svgMarkup ? { __html: svgMarkup } : undefined}
      >
        {svgMarkup ? null : (
          <span style={{ fontSize: 'var(--text-meta)', color: 'rgba(15,23,42,0.45)' }}>
            {error ?? t('dashboard.qrCard.drawing')}
          </span>
        )}
      </div>

      <footer style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'rgba(15,23,42,0.55)',
            margin: 0,
          }}
        >
          {t('dashboard.qrCard.orVisit')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-body)',
            fontVariantNumeric: 'tabular-nums',
            wordBreak: 'break-all',
            margin: 0,
          }}
        >
          {humanUrl(url)}
        </p>
      </footer>

      <style>{`
        /* The qrcode library returns a complete <svg> with its own width/height
           attributes. Those win over the container, so without this the square
           shrinks and the QR inside it does not — which is how a "responsive"
           fix silently produces a clipped, unscannable code. */
        .qr-print-card svg {
          display: block;
          width: 100%;
          height: 100%;
        }
        @media print {
          @page { margin: 16mm; }
          body { background: #ffffff !important; }
          .qr-print-card {
            box-shadow: none !important;
            border: none !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </article>
  );
}

function humanUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return url;
  }
}
